import type { Invoice } from '@/shared/domain'
import { formatCurrency, formatDate } from '@/shared/infra/format'
import { InvoiceDisputeBadge } from './invoice-dispute-badge'
import { InvoiceStatusBadge } from './invoice-status-badge'
import { INVOICE_TYPE_LABELS, PAYMENT_METHOD_LABELS } from './payment-labels'
import { PaymentDetailPanel } from './payment-detail-panel'
import type { PaymentRowActionHandlers } from './payment-row'
import { PaymentRowActions } from './payment-row-actions'

export type PaymentRegisterMobileListProps = {
  invoices: Invoice[]
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  highlightedId?: string | null
  actions: PaymentRowActionHandlers
}

function formatDueDate(invoice: Invoice): string {
  if (invoice.status === 'scheduled') {
    return invoice.scheduledSendDate
      ? `Delivers ${formatDate(invoice.scheduledSendDate)}`
      : '—'
  }
  return invoice.dueDate
    ? `Due ${formatDate(invoice.dueDate)}`
    : '—'
}

export function PaymentRegisterMobileList({
  invoices,
  expandedIds,
  onToggleExpand,
  highlightedId,
  actions,
}: PaymentRegisterMobileListProps) {
  return (
    <div className="flex flex-col gap-2">
      {invoices.map((invoice) => {
        const isExpanded = expandedIds.has(invoice.id)
        return (
          <div
            key={invoice.id}
            data-row-id={invoice.id}
            className={`rounded-lg border bg-card p-3 ${
              invoice.id === highlightedId ? 'bg-accent/10 ring-2 ring-accent' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 flex-col">
                <span className="truncate text-sm font-medium">
                  {invoice.clientName ?? invoice.recipientEmail ?? '—'}
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(invoice.createdAt)} ·{' '}
                  {INVOICE_TYPE_LABELS[invoice.invoiceType]}
                  {invoice.paymentMethod
                    ? ` · ${PAYMENT_METHOD_LABELS[invoice.paymentMethod]}`
                    : ''}
                </span>
              </div>
              <span className="whitespace-nowrap text-sm font-medium">
                {formatCurrency(invoice.amount)}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <InvoiceStatusBadge
                status={invoice.status}
                emailDeliveryStatus={invoice.emailDeliveryStatus}
              />
              {invoice.dispute ? (
                <InvoiceDisputeBadge dispute={invoice.dispute} />
              ) : null}
              <span className="text-xs text-muted-foreground">
                {formatDueDate(invoice)}
              </span>
            </div>

            <div className="mt-2 flex flex-wrap justify-end">
              <PaymentRowActions
                invoice={invoice}
                isExpanded={isExpanded}
                sendingId={actions.sendingId}
                resendingId={actions.resendingId}
                downloadingId={actions.downloadingId}
                viewingReceiptId={actions.viewingReceiptId}
                editingId={actions.editingId}
                markingAsPaidId={actions.markingAsPaidId}
                onSend={actions.onSend}
                onResend={actions.onResend}
                onDownloadPdf={actions.onDownloadPdf}
                onViewReceipt={actions.onViewReceipt}
                onRequestVoid={actions.onRequestVoid}
                onRequestRefund={actions.onRequestRefund}
                onRequestEdit={actions.onRequestEdit}
                onRequestExtend={actions.onRequestExtend}
                onMarkAsPaid={actions.onMarkAsPaid}
                onToggleExpand={onToggleExpand}
              />
            </div>

            {isExpanded ? (
              <div data-row-id={`${invoice.id}-detail`} className="mt-2">
                <PaymentDetailPanel
                  invoice={invoice}
                  orgId={actions.orgId}
                  onGetInvoiceById={actions.onGetInvoiceById}
                  onGetStripeLoginLink={actions.onGetStripeLoginLink}
                />
              </div>
            ) : null}
          </div>
        )
      })}
    </div>
  )
}
